import { Trans, useTranslation } from 'next-i18next';

import { PUBLIC_IMAGES } from '../../../../config';

import GMap from '@/components/GMap/dynamic';
import { Icons, IconsTypes } from '@/components/shared/Icons';
import { TicketsSlider } from '@/components/TicketsSlider';
import { COMMON_LOCALE_PART } from '@/locale';
import { ITicketSectionInfo } from '@/types';

interface IPlanYourVisit {
  entryTickets: ITicketSectionInfo | null;
  additionalServices: ITicketSectionInfo | null;
  excursions: ITicketSectionInfo | null;
}

export function PlanYourVisit({ entryTickets, additionalServices, excursions }: IPlanYourVisit) {
  const { t } = useTranslation(COMMON_LOCALE_PART);

  return (
    <>
      <section>
        <div className="flex flex-col gap-5 md:flex-row">
          <div className="md:w-1/2">
            <h3 className="mb-4 text-center md:text-left ">{t('shared.schedule.title')}</h3>
            <div className="flex items-center gap-3 rounded-lg bg-white p-4">
              <span className="mt-2">
                <Icons name={IconsTypes.CLOCK} />
              </span>
              <p className="text-base-md font-bold">
                <Trans i18nKey="shared.schedule.time" />
              </p>
            </div>
          </div>

          <div className="mt-9 md:mt-0 md:w-1/2">
            <h3 className="mb-4 text-center md:text-left">{t('shared.location.title')}</h3>
            <div className="flex items-center gap-3 rounded-lg bg-white p-4">
              <span className="mt-2">
                <Icons name={IconsTypes.PIN} />
              </span>
              <p className="font-bold">
                {t('shared.location.address')}
                <br />
                <span className="font-normal">{t('plan-visit.location-caption')}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section
        id="route"
        className="mx-auto flex w-full flex-col justify-start pb-9 pt-9 [&>div]:h-[266px] [&>div]:w-full"
      >
        <GMap markerTitle={t('shared.location.marker-title')} />
        <p className="mt-9 text-center text-xl font-bold md:text-left">{t('plan-visit.how-to-travel')}</p>
      </section>

      <section className="mb-24 flex flex-col md:flex-row">
        <div className="flex md:w-1/2 ">
          <div>{Icons({ name: IconsTypes.CAR })}</div>
          <p className="ml-4 max-w-[285px]">
            <span className="font-bold">{t('plan-visit.travel-car')}</span>
            <br />
            <br />
            {t('plan-visit.travel-car1')}
            <br />
            <br />
            {t('plan-visit.travel-car2')}
            <br />
            <br />
            {t('plan-visit.travel-car3')}
          </p>
        </div>

        <div className="flex flex-col md:w-1/2">
          <div className="mt-4 flex md:mt-0">
            <div>{Icons({ name: IconsTypes.BUS })}</div>
            <div className="ml-4">
              <p className="font-bold">{t('plan-visit.travel-bus')}</p>
              <br />
              <p>{t('plan-visit.travel-bus-address1')}</p>
              <ul className="mt-3 list-disc">
                <li className="ml-5">{t('plan-visit.travel-bus-address2')}</li>
                <li className="ml-5">{t('plan-visit.travel-bus-address3')}</li>
                <li className="ml-5">{t('plan-visit.travel-bus-address4')}</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 flex ">
            <div>{Icons({ name: IconsTypes.BICYCLE })}</div>
            <p className="ml-4">
              <span className="font-bold">{t('plan-visit.travel-bicycle')}</span>
              <br />
              <br />
              {t('plan-visit.travel-bicycle1')}
            </p>
          </div>
        </div>
      </section>
      {/*
        IF client would add any new section, this should be
        rewritten in array like way, so all new sections
        that arent being verified on parent level could be
        seen here
      */}
      {/* ENTRY TICKETS SECTION */}
      {entryTickets && (
        <section>
          <h2 className="mb-9 text-center md:text-left md:text-[32px]">{t('plan-visit.ticket-prices-title')}</h2>
          <hr className="mb-9" />
          {entryTickets.show_name && <h3 className="mb-9 text-center text-xl"> {entryTickets.name}:</h3>}
          <TicketsSlider tickets={entryTickets.tickets} />
          <p>{t('plan-visit.entry-weekend2')}</p>
          <br />
          <h5
            className="mb-3 font-bold"
            dangerouslySetInnerHTML={{ __html: t('plan-visit.entry-exception') ?? '' }}
          ></h5>
          <ul className="mb-8 ml-5 list-disc">
            <li>{t('plan-visit.entry-exception1')}</li>
            <li>{t('plan-visit.entry-exception2')}</li>
          </ul>
          <h5 className="mb-3 font-bold">{t('plan-visit.entry-special')}</h5>
          <ul className="mb-3 ml-5 list-disc">
            <li>{t('plan-visit.entry-special1')}</li>
            <li>{t('plan-visit.entry-special2')}</li>
          </ul>
          <p className="mb-9">{t('plan-visit.entry-info')}</p>
        </section>
      )}
      {/* ADDITIONAL SERVICE SECTION */}
      {additionalServices && (
        <section>
          <hr className="mb-9" />
          {additionalServices.show_name && <h3 className="mb-9 text-center text-xl"> {additionalServices.name}:</h3>}
          <h4 className="m-auto mb-7 max-w-md text-center text-base md:text-md">{additionalServices.annotation}</h4>
          <h4 className="mb-9 text-base md:text-[18px]">{additionalServices.description}</h4>
          <TicketsSlider tickets={additionalServices.tickets} />

          <p className="mb-9">{t('plan-visit.services-info')}</p>
        </section>
      )}
      {/* EXCURSIONS/TOURS SECTION */}
      {excursions && (
        <section>
          <hr className="mb-9" />

          <h5 className="mb-9 text-base font-bold md:text-md">{excursions.description}</h5>

          {excursions.show_name && <h3 className="mb-9 text-center text-xl"> {excursions.name}:</h3>}

          <TicketsSlider tickets={excursions.tickets} />
        </section>
      )}

      <hr className="mb-12 mt-16" />

      <section className="flex flex-col rounded-lg bg-white px-3 py-3 md:flex-row md:items-center">
        <ol className="ml-7 list-disc">
          <li className="list-none">{t('plan-visit.footer-text1')}</li>
        </ol>
        <div
          className="h-48 w-full bg-contain bg-center bg-no-repeat md:w-96"
          style={{ backgroundImage: `url(${PUBLIC_IMAGES.PLAN_YOUR_VISIT})` }}
        />
      </section>
    </>
  );
}
